/**
 * Undo/Redo middleware for StudioMcpBus.
 *
 * Intercepts every MCP command, extracts the `_inverse` field from
 * successful results, and manages undo/redo stacks with debounce
 * merging and transaction grouping.
 */

import type { McpCommand, McpResult, Middleware } from './types'
import { STUDIO_NS } from './types'
import type { UndoEntry, UndoState } from './undo-types'
import { UNDO_CONFIG } from './undo-types'
import type { StudioMcpBus } from './studio-mcp-bus'

const UNDO_TOOL = `${STUDIO_NS}undo`
const REDO_TOOL = `${STUDIO_NS}redo`

export interface StudioUndoOwnershipReceipt {
  capabilityPlanId: string
  undoEntryId: string
}

type OwnedUndoEntry = UndoEntry & { undoEntryId: string }

// Ownership is process-local recovery authority. Keeping it beside the result
// in a WeakMap lets the orchestration handler consume it without placing the
// receipt in MCP data, notifications, journal events, or checkpoints.
const ownershipReceipts = new WeakMap<McpResult, StudioUndoOwnershipReceipt>()

export function consumeStudioUndoOwnershipReceipt(
  result: McpResult,
): StudioUndoOwnershipReceipt | null {
  const receipt = ownershipReceipts.get(result) ?? null
  ownershipReceipts.delete(result)
  return receipt
}

/** Rough memory estimate for an entry (UTF-16 bytes). */
function estimateBytes(entry: UndoEntry): number {
  return JSON.stringify(entry).length * 2
}

/** Monotonic counter for commands with no target identity — a fresh value
 * per mergeKey() call guarantees those entries never debounce-merge. */
let nonMergeableSeq = 0

/** Extract the merge key from a command (tool + target identifier).
 *
 * Data tools address sub-entries of a block (`name` for variables and
 * datasources, `eventKey` for bindings), so the key includes them — two
 * rapid adds of DIFFERENT variables on one block must not merge (merging
 * keeps only the first inverse, silently dropping the second undo).
 * Commands with no identity at all (addFlow/addNode/addConnection) get a
 * unique key: merging distinct creations is always wrong. */
function mergeKey(cmd: McpCommand): string {
  const elementId = cmd.params.elementId as string | undefined
  const flowId =
    cmd.tool === `${STUDIO_NS}deleteFlow`
      ? (cmd.params.flowId as string | undefined)
      : undefined
  const blockName = cmd.params.blockName as string | undefined
  const sub = (cmd.params.name ?? cmd.params.eventKey) as string | undefined
  const capabilityPlanId = cmd.params._capabilityPlanId as string | undefined
  const target =
    elementId ??
    flowId ??
    (blockName ? (sub ? `${blockName}/${sub}` : blockName) : `uniq:${++nonMergeableSeq}`)
  return `${cmd.tool}::${target}::capability:${capabilityPlanId ?? 'none'}`
}

/**
 * Create the undo middleware and state accessor.
 *
 * Usage:
 * ```ts
 * const undo = createUndoMiddleware()
 * bus.use(undo.middleware)
 * registerUndoTools(bus, undo)
 * ```
 */
export interface UndoMiddlewareOptions {
  /** Called synchronously after every stack mutation (push, undo, redo). */
  onStackChange?: (state: UndoState) => void
}

export function createUndoMiddleware(options?: UndoMiddlewareOptions) {
  const undoStack: OwnedUndoEntry[] = []
  const redoStack: OwnedUndoEntry[] = []

  // Transaction state
  let activeTransaction: OwnedUndoEntry[] | null = null
  let transactionFirstInverse: McpCommand | null = null

  // Flag: when true, the middleware skips recording (used during undo/redo execution)
  let executing = false

  /** Warn once when threshold is crossed (reset when stack shrinks). */
  let warned = false

  function totalBytes(): number {
    return undoStack.reduce((sum, e) => sum + estimateBytes(e), 0)
  }

  function enforceLimit(): void {
    while (undoStack.length > UNDO_CONFIG.MAX_ENTRIES) {
      undoStack.shift() // drop oldest
    }

    // CS-4: byte budget. F4/F3 inverses carry whole-buffer / whole-flow
    // snapshots, so the count cap alone admits O(MAX_ENTRIES × snapshot)
    // retained memory. Evict oldest-first until under MAX_BYTES — but always
    // keep the newest entry, so undo of the LAST action works even when that
    // single entry is oversized.
    let bytes = totalBytes()
    while (undoStack.length > 1 && bytes > UNDO_CONFIG.MAX_BYTES) {
      const dropped = undoStack.shift()
      if (dropped) bytes -= estimateBytes(dropped)
    }

    // Warn at 80% capacity
    if (undoStack.length >= UNDO_CONFIG.MAX_ENTRIES * UNDO_CONFIG.WARN_THRESHOLD) {
      if (!warned) {
        console.warn(
          `[undo] Stack at 80% capacity (${undoStack.length}/${UNDO_CONFIG.MAX_ENTRIES} entries)`,
        )
        warned = true
      }
    } else {
      warned = false
    }
  }

  function pushEntry(entry: OwnedUndoEntry): OwnedUndoEntry {
    const last = undoStack[undoStack.length - 1]

    // Debounce merge: same tool + same target within DEBOUNCE_MS
    if (
      last &&
      !entry.transactionId &&
      mergeKey(entry.forward) === mergeKey(last.forward) &&
      entry.timestamp - last.timestamp < UNDO_CONFIG.DEBOUNCE_MS
    ) {
      // Keep original inverse (revert to state before first rapid command),
      // update forward to latest command and timestamp
      last.forward = entry.forward
      last.timestamp = entry.timestamp
      return last
    }

    undoStack.push(entry)
    enforceLimit()
    return entry
  }

  function notifyChange(): void {
    options?.onStackChange?.(getState())
  }

  function getState(): UndoState {
    const top = undoStack[undoStack.length - 1]
    const topForward = top?.children?.at(-1)?.forward ?? top?.forward
    const topCapabilityPlanId =
      typeof topForward?.params._capabilityPlanId === 'string' ? topForward.params._capabilityPlanId : undefined
    return {
      canUndo: undoStack.length > 0,
      canRedo: redoStack.length > 0,
      stackSize: undoStack.length,
      estimatedBytes: totalBytes(),
      ...(topCapabilityPlanId ? { topCapabilityPlanId } : {}),
    }
  }

  /** Execute a command on the bus bypassing undo recording. */
  async function executeBypass(
    bus: StudioMcpBus,
    cmd: McpCommand,
  ): Promise<McpResult> {
    executing = true
    try {
      return await bus.execute(cmd)
    } finally {
      executing = false
    }
  }

  // Expose bus ref for undo/redo tool handlers
  let busRef: StudioMcpBus | null = null

  /**
   * Close the open transaction as a single grouped entry. Called on
   * `_transactionEnd` and whenever a command fails (or ends the transaction
   * without an inverse) — leaving a transaction dangling would silently
   * swallow every subsequent entry into it, killing undo for the session.
   */
  function closeActiveTransaction(): OwnedUndoEntry | null {
    if (!activeTransaction) return null
    const collected = activeTransaction
    const firstInverse = transactionFirstInverse
    activeTransaction = null
    transactionFirstInverse = null
    if (collected.length === 0 || !firstInverse) return null
    const grouped: OwnedUndoEntry = {
      undoEntryId: crypto.randomUUID(),
      forward: collected[collected.length - 1].forward,
      inverse: firstInverse,
      timestamp: collected[0].timestamp,
      children: [...collected],
      ...(collected.some((entry) => entry.redoPolicy === 'confirmation-required')
        ? { redoPolicy: 'confirmation-required' as const }
        : {}),
    }
    // Clear redo on new action
    redoStack.length = 0
    const pushed = pushEntry(grouped)
    notifyChange()
    return pushed
  }

  const middleware: Middleware = async (
    cmd: McpCommand,
    next: () => Promise<McpResult>,
  ): Promise<McpResult> => {
    // Skip recording for undo/redo tools and bypass-flagged executions
    if (executing || cmd.tool === UNDO_TOOL || cmd.tool === REDO_TOOL) {
      return next()
    }

    const result = await next()

    if (!result.success) {
      // A failed command may not leave a transaction dangling open.
      closeActiveTransaction()
      return result
    }

    // Extract _inverse from result.data
    const data = result.data as Record<string, unknown> | undefined
    if (!data || !data._inverse) {
      // No inverse to record — but a transaction-ending command must still
      // close the group it belongs to.
      if (activeTransaction && cmd.params._transactionEnd) closeActiveTransaction()
      return result
    }

    const inverse = data._inverse as McpCommand
    // Strip _inverse from result before returning to caller
    const { _inverse, _redoPolicy, ...cleanData } = data
    const cleanResult: McpResult = { ...result, data: cleanData }

    const entry: OwnedUndoEntry = {
      undoEntryId: crypto.randomUUID(),
      forward: { ...cmd },
      inverse,
      timestamp: Date.now(),
      ...(_redoPolicy === 'confirmation-required'
        ? { redoPolicy: 'confirmation-required' as const }
        : {}),
    }

    // Transaction handling
    if (cmd.params._transactionStart) {
      activeTransaction = [entry]
      transactionFirstInverse = inverse
    } else if (activeTransaction) {
      activeTransaction.push(entry)
      if (cmd.params._transactionEnd) {
        const grouped = closeActiveTransaction()
        const childPlanIds = grouped?.children?.map(
          (child) => child.forward.params._capabilityPlanId,
        ) ?? []
        const capabilityPlanId = childPlanIds[0]
        const hasSingleOwner =
          childPlanIds.length > 0 &&
          typeof capabilityPlanId === 'string' &&
          capabilityPlanId.length > 0 &&
          childPlanIds.every((candidate) => candidate === capabilityPlanId)
        if (grouped && hasSingleOwner) {
          ownershipReceipts.set(cleanResult, {
            capabilityPlanId,
            undoEntryId: grouped.undoEntryId,
          })
        }
        return cleanResult
      }
    } else {
      // Normal (non-transaction) entry — clear redo stack
      redoStack.length = 0
      const pushed = pushEntry(entry)
      notifyChange()
      const capabilityPlanId = cmd.params._capabilityPlanId
      if (typeof capabilityPlanId === 'string' && capabilityPlanId) {
        ownershipReceipts.set(cleanResult, {
          capabilityPlanId,
          undoEntryId: pushed.undoEntryId,
        })
      }
    }

    return cleanResult
  }

  function doUndo(): { entry: UndoEntry } | null {
    const entry = undoStack.pop()
    if (!entry) return null
    redoStack.push(entry)
    notifyChange()
    return { entry }
  }

  function assertUndoOwnership(params: Record<string, unknown>): void {
    const expectedPlanId = params._expectedCapabilityPlanId
    const expectedEntryId = params._expectedUndoEntryId
    const hasPlanField = Object.prototype.hasOwnProperty.call(
      params,
      '_expectedCapabilityPlanId',
    )
    const hasEntryField = Object.prototype.hasOwnProperty.call(
      params,
      '_expectedUndoEntryId',
    )
    if (
      hasPlanField !== hasEntryField ||
      (hasPlanField &&
        (typeof expectedPlanId !== 'string' ||
          !expectedPlanId ||
          typeof expectedEntryId !== 'string' ||
          !expectedEntryId))
    ) {
      throw new Error(
        'undo_ownership_mismatch: expected capability plan and undo entry must be supplied together',
      )
    }
    if (!hasPlanField) return
    const top = undoStack.at(-1)
    const topForward = top?.children?.at(-1)?.forward ?? top?.forward
    if (
      !top ||
      top.undoEntryId !== expectedEntryId ||
      topForward?.params._capabilityPlanId !== expectedPlanId
    ) {
      throw new Error(
        'undo_ownership_mismatch: current undo stack top is not owned by this capability plan',
      )
    }
  }

  function doRedo(): { entry: UndoEntry } | null {
    const entry = redoStack.pop()
    if (!entry) return null
    undoStack.push(entry)
    notifyChange()
    return { entry }
  }

  /** Roll an entry back from the redo stack to the undo stack after a
   * failed inverse execution — the state did not revert, so the entry must
   * stay undoable and the tool must report failure. */
  function rollbackUndo(completedChildren = 0): void {
    const entry = redoStack.pop()
    if (entry) {
      const remainingChildren = entry.children?.slice(
        0,
        Math.max(0, entry.children.length - completedChildren),
      )
      undoStack.push({
        ...entry,
        ...(remainingChildren
          ? {
              children: remainingChildren,
              forward: remainingChildren.at(-1)?.forward ?? entry.forward,
            }
          : {}),
        // Once any inverse reports failure, the original transaction can no
        // longer be replayed as one known-good unit. A later successful retry
        // may finish the rollback, but it must not create a global Redo entry.
        discardRedoOnUndo: true,
      })
    }
    notifyChange()
  }

  function discardLatestRedo(): void {
    if (redoStack.pop()) notifyChange()
  }

  /** Inverse of rollbackUndo, for a failed redo. */
  function rollbackRedo(): void {
    const entry = undoStack.pop()
    if (entry) redoStack.push(entry)
    notifyChange()
  }

  function dropConfirmationRequiredRedo(): boolean {
    const entry = redoStack[redoStack.length - 1]
    if (entry?.redoPolicy !== 'confirmation-required') return false
    redoStack.pop()
    notifyChange()
    return true
  }

  /** Project-scope reset (PI-3): clear BOTH stacks (and any open transaction)
   * and notify. The stacks are session-lifetime module state while hydrated
   * flow ids (`<block>:<name>`) COLLIDE across template-derived projects —
   * without a clear on project switch, Cmd+Z in project B replays (and
   * PERSISTS, via restoreFlowSteps → recoverFlowSteps → auto-save) project
   * A's snapshots into B. Called from flow-editor-store.ensureProject. */
  function clearAll(): void {
    undoStack.length = 0
    redoStack.length = 0
    activeTransaction = null
    transactionFirstInverse = null
    notifyChange()
  }

  return {
    middleware,
    getState,
    clearAll,
    /** @internal — used by registerUndoTools */
    _doUndo: doUndo,
    _assertUndoOwnership: assertUndoOwnership,
    _doRedo: doRedo,
    _rollbackUndo: rollbackUndo,
    _rollbackRedo: rollbackRedo,
    _discardLatestRedo: discardLatestRedo,
    _dropConfirmationRequiredRedo: dropConfirmationRequiredRedo,
    _setBus: (bus: StudioMcpBus) => {
      busRef = bus
    },
    _getBus: () => busRef,
    _executeBypass: executeBypass,
  }
}

export type UndoMiddlewareHandle = ReturnType<typeof createUndoMiddleware>

/**
 * Register `cogo.studio.undo` and `cogo.studio.redo` tools on the bus.
 */
export function registerUndoTools(
  bus: StudioMcpBus,
  handle: UndoMiddlewareHandle,
): void {
  handle._setBus(bus)

  bus.registerTool('undo', {
    description: 'Undo the last command',
    metadata: { tags: ['undo', 'internal'] },
    inputSchema: {
      type: 'object',
      properties: {
        _discardRedo: { type: 'boolean' },
        _expectedCapabilityPlanId: { type: 'string' },
        _expectedUndoEntryId: { type: 'string' },
      },
      additionalProperties: false,
    },
    handler: async (params) => {
      handle._assertUndoOwnership(params)
      const result = handle._doUndo()
      if (!result) {
        throw new Error('Nothing to undo')
      }
      const { entry } = result
      let completedChildren = 0

      // Execute inverse commands — a rejected inverse (e.g. validation
      // middleware runs on bypass executions too) means state did NOT
      // revert: roll the entry back and report failure instead of a
      // silent { undone: true } that would let redo double-apply.
      try {
        if (entry.children && entry.children.length > 0) {
          // Transaction: execute all children's inverses in reverse order
          for (let i = entry.children.length - 1; i >= 0; i--) {
            const res = await handle._executeBypass(bus, entry.children[i].inverse)
            if (!res.success) throw new Error(res.error ?? 'Undo step failed')
            completedChildren += 1
          }
        } else {
          const res = await handle._executeBypass(bus, entry.inverse)
          if (!res.success) throw new Error(res.error ?? 'Undo failed')
        }
      } catch (err) {
        handle._rollbackUndo(completedChildren)
        throw err instanceof Error ? err : new Error(String(err))
      }

      if (params._discardRedo === true || entry.discardRedoOnUndo) {
        handle._discardLatestRedo()
      }

      return { undone: true }
    },
  })

  bus.registerTool('redo', {
    description: 'Redo the last undone command',
    metadata: { tags: ['redo', 'internal'] },
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    handler: async () => {
      if (handle._dropConfirmationRequiredRedo()) {
        throw new Error(
          'Redo requires a fresh destructive-operation preview and user confirmation.',
        )
      }
      const result = handle._doRedo()
      if (!result) {
        throw new Error('Nothing to redo')
      }
      const { entry } = result

      try {
        // Re-execute forward commands
        if (entry.children && entry.children.length > 0) {
          for (const child of entry.children) {
            const res = await handle._executeBypass(bus, child.forward)
            if (!res.success) throw new Error(res.error ?? 'Redo step failed')
          }
        } else {
          const res = await handle._executeBypass(bus, entry.forward)
          if (!res.success) throw new Error(res.error ?? 'Redo failed')
        }
      } catch (err) {
        handle._rollbackRedo()
        throw err instanceof Error ? err : new Error(String(err))
      }

      return { redone: true }
    },
  })
}
